import React, {
  useRef,
  useEffect,
  useState,
  startTransition,
  Suspense,
} from "react";
import {
  Box,
  useTheme,
  IconButton,
  Stack,
  Typography,
  CircularProgress,
} from "@mui/material";
import dynamic from "next/dynamic";
import { useAppStore } from "@/global/store";
import { selectInventory } from "@/global/store/data/purchaseSeats/inventories";
import {
  seatMapState,
  selectView2dInventoryId,
} from "@/global/store/data/seatMap";
import { GroupLevelInventory } from "@/global/store/data/types";
import ZoomInIcon from "@mui/icons-material/ZoomIn";
import ZoomOutIcon from "@mui/icons-material/ZoomOut";
import { useDispatch } from "react-redux";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import { useCssMediaQuery } from "@/shared/base/hooks/useCssMediaQuery";
import { useRouterExtended } from "@/global/hooks/useRouterExtended";
import { useSelectActiveEvent } from "../PurchaseSeats/ChooseSeats/useSelectActiveEvent";

interface CameraState {
  x: number;
  y: number;
  zoom: number;
  isDragging: boolean;
}

interface Seat {
  rowName: string;
  seatNumber: number;
}

// Dynamically import Konva components with SSR disabled
const Stage = dynamic(() => import("react-konva").then((mod) => mod.Stage), {
  ssr: false,
});
const Layer = dynamic(() => import("react-konva").then((mod) => mod.Layer), {
  ssr: false,
});
const Rect = dynamic(() => import("react-konva").then((mod) => mod.Rect), {
  ssr: false,
});
const Circle = dynamic(() => import("react-konva").then((mod) => mod.Circle), {
  ssr: false,
});
const Text = dynamic(() => import("react-konva").then((mod) => mod.Text), {
  ssr: false,
});
const Group = dynamic(() => import("react-konva").then((mod) => mod.Group), {
  ssr: false,
});

export function GroupTicketSeatMap() {
  const router = useRouterExtended();
  const theme = useTheme();
  const { sm: smUp, md: mdUp } = useCssMediaQuery("up");
  const dispatch = useDispatch();
  const containerRef = useRef<HTMLDivElement>(null);
  const [dimensions, setDimensions] = useState({
    width: 0,
    height: 0,
    minSeatNumber: 0,
    maxSeatNumber: 0,
    totalRows: 0,
  });
  const [camera, setCamera] = useState<CameraState>({
    x: 0,
    y: 0,
    zoom: 1,
    isDragging: false,
  });

  const totalRowCount = 50 + dimensions.totalRows;
  const totalColCount =
    50 + dimensions.maxSeatNumber - dimensions.minSeatNumber;
  const [isMounted, setIsMounted] = useState(false);

  const { activeEvent: eventActive } = useSelectActiveEvent();
  const view2dInventoryId = useAppStore(selectView2dInventoryId);
  const inventories = useAppStore(selectInventory(eventActive?.eventCode));
  const selectedInventory = (inventories.find(
    (inv) => inv.id == view2dInventoryId
  ) || null) as GroupLevelInventory | null;
  const seats = selectedInventory?.seats || [];

  // helper function to close the carousel
  const handleCloseCarousel = () => {
    dispatch(
      seatMapState.actions.set({
        viewId3d: "",
        view2dInventoryId: "",
      })
    );
  };

  // Set up mounting and dimensions
  useEffect(() => {
    setIsMounted(true);
    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          ...dimensions,
          width: containerRef.current?.offsetWidth || 0,
          height: smUp ? 400 : 200,
        });
      }
    };

    updateDimensions();
    window.addEventListener("resize", updateDimensions);

    return () => {
      window.removeEventListener("resize", updateDimensions);
    };
  }, []);

  // Organize seats data
  const { seatsByRow, rowNames, minSeatNumber, maxSeatNumber } =
    React.useMemo(() => {
      const seatsByRow: Record<string, Seat[]> = {};
      let minSeatNumber = Infinity;
      let maxSeatNumber = -Infinity;
      let rowNames: string[] = [];

      // if seats are empty, then return empty values to avoid infinite re-renders
      if (seats.length === 0) {
        return { seatsByRow, rowNames, minSeatNumber: 0, maxSeatNumber: 0 };
      }

      seats.forEach((seat) => {
        if (!seatsByRow[seat.rowName]) {
          seatsByRow[seat.rowName] = [];
          rowNames.push(seat.rowName);
        }
        seatsByRow[seat.rowName].push(seat);

        if (seat.seatNumber < minSeatNumber) minSeatNumber = seat.seatNumber;
        if (seat.seatNumber > maxSeatNumber) maxSeatNumber = seat.seatNumber;
      });

      const isNumeric = rowNames.every((name) => !isNaN(Number(name)));
      if (isNumeric) {
        rowNames.sort((a, b) => Number(a) - Number(b));
        const min = Number(rowNames[0]);
        const max = Number(rowNames[rowNames.length - 1]);
        rowNames = [];
        for (let i = min; i <= max; i++) {
          rowNames.push(i.toString());
        }
      } else {
        rowNames.sort((a, b) => a.localeCompare(b));
      }

      setDimensions({
        ...dimensions,
        minSeatNumber,
        maxSeatNumber,
        totalRows: rowNames.length,
      });

      return { seatsByRow, rowNames, minSeatNumber, maxSeatNumber };
    }, [seats]);

  // Calculate layout
  const {
    seatSize,
    spacing,
    centerX,
    centerY,
    totalRows,
    gridWidth,
    gridHeight,
  } = React.useMemo(() => {
    const seatSize = dimensions.width / totalColCount; // Adjusted for spacing
    const spacing = 8;

    const totalSeatNumbers = maxSeatNumber - minSeatNumber + 1;
    const totalRows = rowNames.length;

    const centerX =
      Math.floor(totalColCount / 2) - Math.floor(totalSeatNumbers / 2);
    const centerY = Math.floor(totalRowCount / 2) - Math.floor(totalRows / 2);

    const gridWidth = totalColCount * (seatSize + spacing);
    const gridHeight = totalRowCount * (seatSize + spacing);

    return {
      totalRowCount,
      totalColCount,
      seatSize,
      spacing,
      centerX,
      centerY,
      totalRows,
      gridWidth,
      gridHeight,
    };
  }, [maxSeatNumber, minSeatNumber, rowNames.length, dimensions]);

  // Calculate the minimum zoom level to fit the entire grid with 20% extra space
  const calculateMinZoom = () => {
    if (!dimensions.width || !dimensions.height) return 0.1;

    const availableWidth = dimensions.width;
    const widthZoom = (availableWidth / gridWidth) * 2; // 10% of available width (less zoom out)
    const heightZoom = (dimensions.height / gridHeight) * 2; // 10% of available height (less zoom out)

    return Math.min(widthZoom, heightZoom, 1); // Don't go above 1 for initial view
  };

  // Set initial zoom to fit the grid with 20% extra space
  useEffect(() => {
    if (dimensions.width > 0 && dimensions.height > 0) {
      const minZoom = calculateMinZoom();
      startTransition(() => {
        setCamera((prev) => ({
          ...prev,
          zoom: minZoom,
          x: (dimensions.width - gridWidth * minZoom) / 2,
          y: (dimensions.height - gridHeight * minZoom) / 2,
        }));
      });
    }
  }, [dimensions.width, dimensions.height, gridWidth, gridHeight]);

  if (!isMounted) {
    return (
      <Stack
        ref={containerRef}
        direction={"row"}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          backgroundColor: theme.palette.grey["100"],
          width: "100%",
          height: dimensions.height,
          overflow: "hidden",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
        }}
      >
        <CircularProgress size={"20px"} />
      </Stack>
    );
  }

  return (
    <Box
      ref={containerRef}
      id={"custom-2d-seat-map-container"}
      sx={{
        backgroundColor: theme.palette.grey["50"],
        width: "100%",
        height: dimensions.height,
        // cursor: camera.isDragging ? "grabbing" : "grab",
        overflow: "hidden",
        position: "relative",
        border: `1px solid ${theme.palette.grey[300]}`,
        borderRight: `2px solid ${theme.palette.grey[300]}`,
      }}
    >
      <Box position={"absolute"} top={"8px"} right={"8px"} zIndex={10}>
        <IconButton
          onClick={handleCloseCarousel}
          size="small"
          sx={{
            backgroundColor: `rgba(0, 0, 0, 0.4)`,
            color: `rgba(255, 255, 255, 0.7)`,
            "&:hover": { backgroundColor: `rgba(0, 0, 0, 0.6)` },
          }}
        >
          <CloseRoundedIcon />
        </IconButton>
      </Box>

      <Stack
        direction={"row"}
        justifyContent={"center"}
        alignItems={"center"}
        sx={{
          p: 1,
          position: "absolute",
          width: "100%",
          bottom: 0,
          left: 0,
          zIndex: 9,
        }}
      >
        <Typography color={"text.primary"} fontWeight={600} fontSize={"1rem"}>
          Ticket Group Configuration
        </Typography>
      </Stack>

      {dimensions.width > 0 && (
        <Suspense
          fallback={
            <Box
              sx={{
                width: "100%",
                height: "100%",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              Loading seat map...
            </Box>
          }
        >
          {/* Main Stage */}
          <Stage width={dimensions.width} height={dimensions.height}>
            {/* Main Map Layer - affected by zoom/pan */}
            <Layer
              x={0}
              scaleX={camera.zoom}
              scaleY={camera.zoom}
              offsetX={-camera.x / camera.zoom}
              offsetY={-camera.y / camera.zoom}
            >
              {/* Draw grid */}
              {Array.from({ length: totalRowCount }).map((_, i) =>
                Array.from({ length: totalColCount }).map((_, j) => (
                  <Circle
                    key={`${i}-${j}`}
                    radius={seatSize / 2}
                    x={j * (seatSize + spacing)}
                    y={i * (seatSize + spacing)}
                    fill={theme.palette.grey[200]}
                  />
                ))
              )}

              {/* Draw seats */}
              {rowNames.map((rowName, rowIdx) => {
                const rowSeats = seatsByRow[rowName] || [];
                return rowSeats.map((seat) => {
                  const x = centerX + (seat.seatNumber - minSeatNumber);
                  const y = centerY + rowIdx;

                  if (x >= 0 && y >= 0) {
                    const pxX = x * (seatSize + spacing);
                    const pxY = y * (seatSize + spacing);

                    return (
                      <Circle
                        key={`${rowName}-${seat.seatNumber}`}
                        x={pxX}
                        y={pxY}
                        radius={seatSize / 2}
                        fill={theme.palette.primary.main}
                        stroke={theme.palette.primary.main}
                        zIndex={1}
                      />
                    );
                  }
                  return null;
                });
              })}
            </Layer>
          </Stage>
        </Suspense>
      )}
    </Box>
  );
}
